import React, { useRef, useState } from "react";
import type { HomepageFeedback } from "./HomeHeroFeedback";
import HeroTestimonialCard from "./HeroTestimonialCard";
import styles from "./HeroTestimonials.module.css";

// Matches ModernTestimonialsSection's duplicated track and pointer scrolling,
// with the live card typography and attribution scaled to the hero.
export default function HeroTestimonials({ feedback }: { feedback: HomepageFeedback[] }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; scroll: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  if (!feedback.length) return null;
  const looping = feedback.length > 1;
  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const card = (quote: HomepageFeedback) => <HeroTestimonialCard key={quote.id} feedback={quote} />;
  return <div className={styles.carousel}>
    <div ref={viewportRef} className={`${styles.viewport} ${dragging ? styles.dragging : ""} ${looping ? "" : styles.staticViewport}`} role="region" aria-label="From the C-Shaped community" tabIndex={0}
      onScroll={event => {
        // Keep manual scrolling within one repeated group so resuming the
        // automatic loop cannot expose an empty end of the strip.
        if (!looping) return;
        const group = event.currentTarget.firstElementChild?.firstElementChild as HTMLElement | null;
        const period = group?.offsetWidth || 0;
        if (period && event.currentTarget.scrollLeft >= period) event.currentTarget.scrollLeft %= period;
      }}
      onPointerDown={event => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        drag.current = { id: event.pointerId, x: event.clientX, scroll: event.currentTarget.scrollLeft };
        setDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={event => {
        if (drag.current?.id !== event.pointerId) return;
        const delta = event.clientX - drag.current.x;
        if (Math.abs(delta) < 2) return;
        event.currentTarget.scrollLeft = drag.current.scroll - delta;
        event.preventDefault();
      }}
      onPointerUp={stopDragging} onPointerCancel={stopDragging} onLostPointerCapture={stopDragging}>
      <div className={`${styles.track} ${looping ? "" : styles.staticTrack}`}>
        <div className={styles.group}>{feedback.map(card)}</div>
        {looping && [1, 2].map(copy => <div className={styles.group} aria-hidden="true" key={copy}>{feedback.map(card)}</div>)}
      </div>
    </div>
  </div>;
}
